import { RateSelectPageVariation } from '@mortgage-pos/types'
import ValueProps from '../ValueProps'
import Tooltip from '@mortgage-pos/ui/components/Tooltip'
import React, { FC, useState } from 'react'
import InfoModal from '@mortgage-pos/ui/components/InfoModal'
import RateCardCompare from '../RateCardCompare'
import { environment } from '@mortgage-pos/ui/env'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { iconMap } from '@mortgage-pos/ui/utils/customizationHelpers'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import { whatWeAssume } from '@mortgage-pos/ui/data/bankrateTooltipData'
import { translateAnswersToCPLFormat } from '@mortgage-pos/ui/utils/bankrateCpl'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'

const contentDefaults: RateSelectPageVariation = {
  variation: 'default',
  title: 'We can beat your current offer',
  subTitle: 'The results are in!',
  cta: 'Continue',
}

interface RatesProps {
  submitRate: (rate) => void
}

const RatesPage: FC<RatesProps> = ({ submitRate }) => {
  const alvinRedirectEnabled = useVariation(
    FEATURE_FLAG_TYPES.ALVIN_CPL_REDIRECT
  )
  const { leRate, rawLEData } = useStoreState((state) => state.leComparison)
  const { mergedAnswers } = useStoreState((state) => state.answers)
  const { ratesPage } = useStoreState((state) => state.questionnaire)
  const { sortedBankrateRates, currentMortgageDetails } = useStoreState(
    (state) => state.bankrateCompare
  )
  const [showDisclosureModal, setShowDisclosureModal] = useState(false)

  const { elementClicked } = useStoreActions((store) => store.tracking)

  // Use page content coming from Storyblok, with a fallback to default values
  const pageContent = ratesPage?.variations?.[0] || contentDefaults

  const matchedRates = {
    rates: [sortedBankrateRates[0], sortedBankrateRates[1]],
    rateItems: [mergedAnswers.refiReason, mergedAnswers.refiReason],
  }

  const handleSeeOptionsClick = (e: React.SyntheticEvent) => {
    e.preventDefault()

    newRelic.increment('compare_rates_page.click_other_options')

    elementClicked({
      elementType: 'Button',
      location: 'Compare Rates page',
      actionOutcome: 'Routes user to Mortgage Lead Form',
      text: 'Continue to see more options',
    })

    const mortgageLeadFormBaseUrl = environment.bankrateMortgageLeadFormBaseUrl

    const leadParams = new URLSearchParams()

    leadParams.append('zipcode', mergedAnswers.zipCode)
    leadParams.append('show_15S2S_content', 'true')

    const urlParams = new URLSearchParams(window.location.search)
    leadParams.append(
      'utm_15stslocation',
      urlParams.get('utm_15stslocation') || ''
    )

    if (alvinRedirectEnabled) {
      const cplData = translateAnswersToCPLFormat({
        mergedAnswers,
        currentMortgageDetails,
      })
      leadParams.append('customization', 'alvin')
      leadParams.append('alvin-data', JSON.stringify(cplData))
    }

    const redirectUrl = mortgageLeadFormBaseUrl + '?' + leadParams.toString()

    return window.open(redirectUrl, '_blank')
  }

  const leRateInfo = leRate as any // object type from store doesn't play nicely here
  const termYear =
    mergedAnswers.currentLoanTerm?.toString() ||
    (currentMortgageDetails.paymentScheduleMonthCount / 12)?.toString()

  const currentLoanInfo = {
    ...leRateInfo,
    principleAndInterestPayment:
      mergedAnswers.monthlyPayment || currentMortgageDetails.paymentDueAmount,
    apr:
      leRateInfo.apr ||
      parseFloat(mergedAnswers.interest) * 100 ||
      parseFloat(currentMortgageDetails?.estimatedInterestRate),
    loanAmount: leRateInfo.loanAmount || mergedAnswers.remainingBalance,
    term: termYear ? termYear + ' year' : null,
  }

  return (
    <>
      {showDisclosureModal ? (
        <InfoModal
          onClose={() => setShowDisclosureModal(false)}
          modalVariant="disclosures"
        />
      ) : null}

      <div
        className="compare-page__disclosure-link"
        onClick={() => setShowDisclosureModal(true)}
      >
        Advertiser Disclosure
      </div>
      <h4
        className="compare-page__subtitle"
        data-testid="rate-selection-header"
      >
        {pageContent.subTitle}
      </h4>

      <h1 className="compare-page__title">
        {pageContent.title}
        <Tooltip
          className="compare-page__legal-tooltip"
          title={whatWeAssume.title}
          text={whatWeAssume.text}
        />
      </h1>
      <div className={`rate-selections__options-list --row --bankrate`}>
        <RateCardCompare
          rate={currentLoanInfo}
          superlativeName={'currentLoan'}
          modifierClass={'--other-lender'}
          lender={rawLEData?.lender || 'Other Lender'}
        />

        <RateCardCompare
          rate={matchedRates.rates[0]}
          superlativeName={matchedRates.rateItems[0]}
          handleSubmitRateClick={() => submitRate(matchedRates.rates[0])}
          cta={pageContent.cta}
          bestRate={true}
        />
        <RateCardCompare
          rate={matchedRates.rates[1]}
          superlativeName={matchedRates.rateItems[1]}
          handleSubmitRateClick={() => submitRate(matchedRates.rates[1])}
          cta={pageContent.cta}
        />
      </div>
      <div className="compare-page__button-wrapper">
        <button
          className="br-file-upload__cta-next link"
          onClick={handleSeeOptionsClick}
        >
          Continue to see more options
          <img
            className="br-file-upload__cta-next-arrow"
            src={iconMap('rightArrow')}
            alt="Arrow right icon"
          />
        </button>
      </div>
      <ValueProps />
    </>
  )
}

export default RatesPage
